import { ASSIGNED_SEATING, common } from "@shared/constants";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { showTicketButtons } from "@shared/modules/events/utils/detail-page-helpers";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { SeatWithLocationDataType } from "@shared/modules/seating/types/seating-types";
import { isOnSaleTicketType } from "@shared/modules/ticket-types/utils/ticket-type-utils";
import { useCallback, useMemo } from "react";
import { WaitlistSelectProps } from "./WaitlistSelect.props";

export function useWaitlistSelect(props: WaitlistSelectProps) {
  const {
    item,
    itemsOnSeat,
    voucherData,
    ticketAuthArray,
    updateTicketAuthArray,
    selectItemPermission,
    getCurrentSelectedAmount,
  } = props;

  const { stageServiceState, seatingServiceState, seatingEventData } =
    useSeatingState();
  const purchaseLimits = seatingServiceState.overridePurchaseLimits;
  const { mapItemType } = seatingServiceState;

  const { isBasketUpdating, purchaseSourcePlatform, paymentType } =
    useBasketService();

  const isGroupSale = useMemo(
    () => seatingServiceState.isGroupSalesMap(),
    [seatingServiceState]
  );
  const isDynamicHold = useMemo(
    () => Boolean(seatingServiceState.basketHoldID),
    [seatingServiceState]
  );

  const showButtons = useMemo(
    () =>
      showTicketButtons(
        item,
        seatingEventData,
        voucherData.userVouchers,
        ticketAuthArray,
        updateTicketAuthArray,
        voucherData.basketVoucherCode,
        isDynamicHold
      ) ||
      isGroupSale ||
      isDynamicHold ||
      mapItemType === common.SeatingMapItemType.MEMBERSHIP,
    [showTicketButtons, isGroupSale, isDynamicHold, mapItemType]
  );

  const getMinPurchaseLimit = useCallback(() => {
    if (purchaseLimits) {
      return purchaseLimits.min;
    }
    if (item.minimum_purchase_limit) {
      return item.minimum_purchase_limit;
    }
    // 1 is the default minimum purchase limit for items
    return item.$countOptions?.[1] ?? common.DEFAULT_MIN_TICKET_PURCHASE_LIMIT;
  }, [purchaseLimits, item]);

  const getMaxPurchaseLimit = useCallback(() => {
    // 8 is the default maximum purchase limit for items
    const defaultValue =
      item.$countOptions?.[item.$countOptions?.length - 1] ??
      common.DEFAULT_MAX_TICKET_PURCHASE_LIMIT;

    if (itemsOnSeat.$data && itemsOnSeat.$name === ASSIGNED_SEATING.SEAT.ID) {
      const itemsData = itemsOnSeat.$data as SeatWithLocationDataType;
      const waitlistMax = stageServiceState.countSeatsInLocation(
        Number(itemsData.locationData?.segment_id),
        item.id
      );
      return Math.min(waitlistMax, defaultValue);
    }
    if (purchaseLimits) {
      return Math.min(purchaseLimits.max, defaultValue);
    }
    if (isGroupSale) {
      const ticketPermissions = itemsOnSeat?.$permissions;
      return ticketPermissions?.[0].usage_remaining ?? 1;
    }

    return defaultValue;
  }, [item, itemsOnSeat, purchaseLimits, isGroupSale]);

  const isDisabledSelector = useMemo(
    () =>
      isBasketUpdating ||
      (!isOnSaleTicketType(item) &&
        mapItemType !== common.SeatingMapItemType.MEMBERSHIP),
    [isBasketUpdating, item, mapItemType]
  );

  const selectWaitlistCallback = (val: number) =>
    selectItemPermission({
      itemTypeId: item.id,
      permissionId: item.permissionId,
      requestAmount: val,
      minPurchaseLimit: getMinPurchaseLimit(),
    });

  return {
    isDisabledSelector,
    showButtons,
    purchaseLimits,
    purchaseSourcePlatform,
    paymentType,
    selectWaitlistCallback,
    getMinPurchaseLimit,
    getMaxPurchaseLimit,
    getCurrentSelectedAmount,
  };
}
